import { useEffect } from 'react';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import SlotImage from '@/components/base/SlotImage';
import { useSiteSettings } from '@/hooks/useSiteSettings';

export default function GivePage() {
  const { data: settings } = useSiteSettings();
  const givingEmail = settings?.emails?.find((e) => e.includes('donat')) || settings?.emails?.[0] || 'donations@tgmci.org';

  useEffect(() => {
    document.title = 'Give — TGMCI';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Header />
      <main>
        {/* Hero */}
        <section className="relative pt-40 pb-24 overflow-hidden bg-background-950 text-center">
          <div className="absolute inset-0">
            <SlotImage
              slot="give_hero"
              fallback="https://storage.helloreaddy.io/project_files/e45ec48e-345c-4840-a547-bcc42428da39/4b9bbad6-29fd-42ea-83aa-a1c3fd98a35c_compressed_WhatsApp-Image-2026-09-12-at-15.29.02.webp"
              alt="Giving background"
              className="w-full h-full object-cover object-top opacity-15"
            />
          </div>
          <div className="relative z-10 max-w-[800px] mx-auto px-4">
            <p className="text-accent-400 font-label text-sm tracking-widest uppercase font-semibold mb-3">Generosity</p>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white leading-tight mb-6">
              Give With a Cheerful Heart
            </h1>
            <div className="w-12 h-1 bg-accent-500 mx-auto mb-6" />
            <p className="text-white/70 text-lg leading-relaxed max-w-2xl mx-auto mb-8">
              Your generosity fuels our mission to empower youth, serve communities, and spread the Gospel across Cameroon, Ghana, and beyond.
            </p>
            <p className="text-accent-400 font-heading text-xl italic">
              &ldquo;Each of you should give what you have decided in your heart to give.&rdquo; — 2 Corinthians 9:7
            </p>
          </div>
        </section>

        {/* Why We Give */}
        <section className="py-20 md:py-28 bg-background-50">
          <div className="max-w-[1100px] mx-auto px-4 md:px-8">
            <div className="text-center mb-14">
              <p className="text-accent-600 font-label text-sm tracking-widest uppercase font-semibold mb-3">Why We Give</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900">The Heart Behind Giving</h2>
              <div className="w-12 h-1 bg-accent-500 mt-5 mx-auto" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { icon: 'ri-heart-3-line', title: 'An Act of Worship', text: 'Giving is an expression of gratitude and trust in God — an act of worship that reflects a generous heart.' },
                { icon: 'ri-hand-heart-line', title: 'Serving the Community', text: 'Your gifts directly support youth programs, community outreach, and the church project fund.' },
                { icon: 'ri-seedling-line', title: 'Sowing for the Kingdom', text: 'Every contribution helps spread the Gospel and build a lasting legacy of faith and transformation.' },
              ].map((item) => (
                <div key={item.title} className="bg-background-100 rounded-xl p-7 border border-background-200 text-center">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-50 text-primary-600 mx-auto mb-4">
                    <i className={`${item.icon} text-2xl`} aria-hidden="true" />
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-foreground-800 mb-3">{item.title}</h3>
                  <p className="text-foreground-500 text-sm leading-relaxed">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Ways to Give */}
        <section className="py-20 md:py-28 bg-background-100">
          <div className="max-w-[1100px] mx-auto px-4 md:px-8">
            <div className="text-center mb-14">
              <p className="text-accent-600 font-label text-sm tracking-widest uppercase font-semibold mb-3">Ways to Give</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900">Choose How to Give</h2>
              <div className="w-12 h-1 bg-accent-500 mt-5 mx-auto" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              <div className="bg-background-50 rounded-xl p-7 border border-background-200">
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-600 text-white mb-4">
                  <i className="ri-building-4-line text-xl" aria-hidden="true" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-foreground-800 mb-2">In Person</h3>
                <p className="text-foreground-500 text-sm leading-relaxed">
                  Give during any of our worship services at our locations in Edea, Cameroon and Accra, Ghana.
                </p>
              </div>

              <div className="bg-background-50 rounded-xl p-7 border border-background-200">
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-600 text-white mb-4">
                  <i className="ri-mail-line text-xl" aria-hidden="true" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-foreground-800 mb-2">Contact Our Finance Team</h3>
                <p className="text-foreground-500 text-sm leading-relaxed mb-3">
                  For bank transfer or mobile money giving, reach out to our team and we&apos;ll provide the details.
                </p>
                <a href={`mailto:${givingEmail}`} className="inline-flex items-center gap-1.5 text-sm font-label font-medium text-primary-600 hover:text-primary-800 transition-colors">
                  <i className="ri-mail-line" aria-hidden="true" />
                  {givingEmail}
                </a>
              </div>
            </div>

            {/* Online giving */}
            <div className="mt-10 bg-accent-100 rounded-2xl border border-accent-300 p-8 text-center max-w-2xl mx-auto">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-500 text-foreground-950 mx-auto mb-4">
                <i className="ri-lock-line text-xl" aria-hidden="true" />
              </div>
              <h3 className="font-heading font-semibold text-lg text-foreground-900 mb-2">Online Giving Coming Soon</h3>
              <p className="text-foreground-700 text-sm leading-relaxed">
                We&apos;re setting up secure online donations through card and PayPal. In the meantime, please use the options above or reach out to our team.
              </p>
              <a href="/contact" className="btn-primary mt-6">
                <i className="ri-mail-line" aria-hidden="true" />
                Support TGMCI
              </a>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-primary-700 text-center px-4">
          <h2 className="font-heading text-3xl font-bold text-white mb-4">Partner With Us</h2>
          <p className="text-white/70 text-base mb-8 max-w-lg mx-auto">
            Every gift, no matter the size, helps us reach more lives with the love of Christ.
          </p>
          <a href="/contact" className="btn-accent">
            <i className="ri-chat-1-line" aria-hidden="true" />
            Get in Touch
          </a>
        </section>
      </main>
      <Footer />
    </div>
  );
}